// src/components/SubscriptionPlans.js

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { db } from "../../index";
import { ref, onValue } from "firebase/database";
import { FaInfoCircle } from "react-icons/fa";
import ContactModal from "./ContactModal"; // Import the new modal
import "./styles.css";

const SubscriptionPlans = () => {
  const { t, i18n } = useTranslation();

  // State for data, loading, and errors
  const [pricingData, setPricingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for managing the modal
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  useEffect(() => {
    setLoading(true);
    // Dynamic path based on the current language
    const dbPath = `/egro-care-pro/newPackages/${i18n.language}`;
    const dataRef = ref(db, dbPath);

    const unsubscribe = onValue(
      dataRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setPricingData(snapshot.val());
          setError(null);
        } else {
          setError(t("Could not find pricing data for the selected language."));
          setPricingData(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error("Firebase read failed: ", err);
        setError(t("Failed to load data. Please try again later."));
        setLoading(false);
      }
    );

    // Cleanup subscription on component unmount
    return () => unsubscribe();
  }, [i18n.language, t]); // Re-fetch when language changes

  const handleCtaClick = (pkg) => {
    setSelectedPackage(pkg);
    setModalOpen(true);
  };

  const formatPrice = (price) => {
    // Get the current language: 'en' or 'ar'
    const lang = i18n.language;

    // Define locale and currency based on language
    const options = {
      ar: { locale: "ar-EG", currency: "EGP" },
      en: { locale: "en-US", currency: "EGP" }, // Still EGP, but in English format
    };

    const { locale, currency } = options[lang] || options.en; // fallback to English

    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
    }).format(price);
  };

  if (loading) {
    return <div className="loading-container">{t("Loading Plans...")}</div>;
  }

  if (error) {
    return <div className="error-container">{error}</div>;
  }

  if (!pricingData) {
    return null; // Or some fallback UI
  }

  return (
    <>
      <ContactModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        contactNumber={pricingData.contact}
        packageName={selectedPackage?.name}
      />
      <div className="plans-page-container">
        <header className="plans-header">
          <h1>{t("Choose the Right Plan for Your Farm")}</h1>
          <p>
            {t("Start with a plan that fits your needs and scale as you grow.")}
          </p>
        </header>

        <div className="pricing-grid">
          {pricingData.package.map((pkg, index) => (
            <div
              key={index}
              className={classNames("plan-column", { popular: index === 1 })}
            >
              <div className="plan-header">
                <img src={pkg.icon} alt={pkg.name} className="plan-icon-img" />
                <h3>{pkg.name}</h3>
                <p>{pkg.description}</p>
              </div>
              <div className="plan-price">
                <span className="price-amount">
                  {formatPrice(pkg.egpPrice)}
                </span>
                <span className="price-unit">/ {t("year")}</span>
              </div>
              <button
                onClick={() => handleCtaClick(pkg)}
                className="plan-cta-btn"
              >
                {t("Choose Plan")}
              </button>
            </div>
          ))}
        </div>

        <div className="extra-features-section">
          <h2 className="section-title">
            {t("All plans include the following features")}
          </h2>
          <div className="features-grid">
            {pricingData.features_.map((feature, index) => (
              <div key={index} className="detail-card">
                <div className="detail-icon-container">
                  <img
                    src={feature.icon}
                    alt={feature.name}
                    className="detail-icon-img"
                  />
                </div>
                <p>{feature.name}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="extra-features-section">
          <h2 className="section-title">{t("Additional Services")}</h2>
          <div className="features-grid">
            {pricingData.service.map((service, index) => (
              <div key={index} className="detail-card">
                <div className="detail-icon-container">
                  <img
                    src={service.icon}
                    alt={service.name}
                    className="detail-icon-img"
                  />
                </div>
                <div className="detail-text-container">
                  <p>{service.name}</p>
                  <span className="detail-price">{service.egpValue}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="notes-section">
          <FaInfoCircle className="notes-icon" />
          <div className="notes-text">
            <h4>{t("Important Notes")}</h4>
            <p>{pricingData.notes[0]}</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default SubscriptionPlans;
